import {
  ArchivePermissions,
  AttachmentSectionPermissions,
  CasePermissions,
  DefaultOrganAcceptanceCriteriaPermissions,
  OfferPermissions,
  OPOsPermissions,
  OrganAcceptanceCriteriaPermissions,
  OrganApprovalPermissions,
  OrganizationPermissions,
  OrganizationUserManagePermissions,
  OrganizationUserPermissions,
  QuotePermissions,
  RootPermissionType,
  ServiceApprovalPermissions,
  TrialPermissions,
  TxCenterORPermissions,
} from '@cp/core/permissions/permissions';
import { excludeRole } from '@cp/core/permissions/util';
import { OrganizationUserPermissionsT, RoleMappingT } from '@cp/core/permissions/types';
import { CustomerRole, CustomerRoleOnly, CustomerRoleT, OpoRoleEnum, OpoRoleOnly } from '@/api/apis/customer';
import { InvoicePermissions } from '@/permissions';

const allRoles: CustomerRoleOnly[] = [
  CustomerRole.TxCenterAdministrator,
  CustomerRole.ReceivingSurgeon,
  CustomerRole.TxCenterCoordinator,
  CustomerRole.AdminAuditor,
  CustomerRole.TxCenterTransplantSupport,
];
const canManageRoles: CustomerRoleOnly[] = [
  CustomerRole.TxCenterAdministrator,
  CustomerRole.ReceivingSurgeon,
  CustomerRole.TxCenterCoordinator,
];

const QUOTE_PERMISSIONS: Record<QuotePermissions, CustomerRoleT[]> = {
  [QuotePermissions.Manage]: [CustomerRole.TxCenterAdministrator, CustomerRole.TxCenterQuoteApprover],
  [QuotePermissions.ViewQuoteApprove]: [CustomerRole.AdminAuditor],
  [QuotePermissions.ViewTabSection]: [
    CustomerRole.TxCenterAdministrator,
    CustomerRole.AdminAuditor,
    CustomerRole.TxCenterQuoteApprover,
  ],
};

const ORGAN_APPROVAL_PERMISSIONS: Record<OrganApprovalPermissions, CustomerRoleT[]> = {
  [OrganApprovalPermissions.View]: [
    CustomerRole.ReceivingSurgeon,
    CustomerRole.TxCenterTransplantSupport,
    CustomerRole.TxCenterOrganApprover,
  ],
  [OrganApprovalPermissions.Manage]: [CustomerRole.ReceivingSurgeon, CustomerRole.TxCenterOrganApprover],
};

const SERVICE_APPROVAL_PERMISSIONS: Record<ServiceApprovalPermissions, CustomerRoleT[]> = {
  [ServiceApprovalPermissions.View]: canManageRoles,
  [ServiceApprovalPermissions.Manage]: canManageRoles,
};

const ORGAN_ACCEPTANCE_CRITERIA_PERMISSIONS: Record<OrganAcceptanceCriteriaPermissions, CustomerRoleT[]> = {
  [OrganAcceptanceCriteriaPermissions.View]: excludeRole(allRoles, CustomerRole.AdminAuditor),
  [OrganAcceptanceCriteriaPermissions.Manage]: canManageRoles,
};

const TX_CENTER_OR_PERMISSIONS: Record<TxCenterORPermissions, CustomerRoleT[]> = {
  [TxCenterORPermissions.Manage]: [CustomerRole.ReceivingSurgeon],
  [TxCenterORPermissions.ManageOnCaseAssigned]: [CustomerRole.TxCenterCoordinator],
};

const ATTACHMENTS_SECTION_PERMISSIONS: Record<AttachmentSectionPermissions, CustomerRoleT[]> = {
  [AttachmentSectionPermissions.View]: excludeRole(allRoles, CustomerRole.TxCenterTransplantSupport),
};

const CASE_PERMISSIONS: Record<
  | CasePermissions
  | QuotePermissions
  | OrganApprovalPermissions
  | ServiceApprovalPermissions
  | OrganAcceptanceCriteriaPermissions
  | TxCenterORPermissions
  | AttachmentSectionPermissions,
  CustomerRoleT[]
> = {
  [CasePermissions.Create]: canManageRoles,
  [CasePermissions.CaseDetailsViewTab]: allRoles,
  [CasePermissions.CaseDetailsManageTab]: canManageRoles,
  [CasePermissions.CaseTeamViewTab]: allRoles,
  [CasePermissions.CaseTeamManageTab]: canManageRoles,
  [CasePermissions.CaseClinicalLogViewTab]: excludeRole(allRoles, CustomerRole.AdminAuditor),
  [CasePermissions.CaseClinicalLogManageTab]: canManageRoles,
  [CasePermissions.CaseActivitiesViewTab]: allRoles,
  ...QUOTE_PERMISSIONS,
  ...ORGAN_APPROVAL_PERMISSIONS,
  ...SERVICE_APPROVAL_PERMISSIONS,
  ...ORGAN_ACCEPTANCE_CRITERIA_PERMISSIONS,
  ...TX_CENTER_OR_PERMISSIONS,
  ...ATTACHMENTS_SECTION_PERMISSIONS,
};

const DEFAULT_ORGAN_ACCEPTANCE_CRITERIA_PERMISSIONS: Record<
  DefaultOrganAcceptanceCriteriaPermissions,
  CustomerRoleT[]
> = {
  [DefaultOrganAcceptanceCriteriaPermissions.View]: excludeRole(allRoles, CustomerRole.AdminAuditor),
  [DefaultOrganAcceptanceCriteriaPermissions.Manage]: excludeRole(canManageRoles, CustomerRole.TxCenterCoordinator),
};
const ORGANISATION_USER_PERMISSIONS: Record<OrganizationUserPermissions, OrganizationUserPermissionsT[]> = {
  [OrganizationUserPermissions.View]: allRoles,
  // Customer can resend invite only to those, whom he can create
  [OrganizationUserPermissions.Create]: [
    {
      [CustomerRole.TxCenterAdministrator]: [
        CustomerRole.TxCenterAdministrator,
        CustomerRole.ReceivingSurgeon,
        CustomerRole.TxCenterCoordinator,
        CustomerRole.AdminAuditor,
        CustomerRole.TxCenterTransplantSupport,
        CustomerRole.TxCenterQuoteApprover,
        CustomerRole.TxCenterOrganApprover,
      ],
      [CustomerRole.ReceivingSurgeon]: [
        CustomerRole.ReceivingSurgeon,
        CustomerRole.TxCenterCoordinator,
        CustomerRole.TxCenterTransplantSupport,
        CustomerRole.TxCenterOrganApprover,
      ],
      [CustomerRole.TxCenterCoordinator]: [
        CustomerRole.ReceivingSurgeon,
        CustomerRole.TxCenterCoordinator,
        CustomerRole.TxCenterTransplantSupport,
      ],
      [CustomerRole.AdminAuditor]: [],
      [CustomerRole.TxCenterTransplantSupport]: [],
      [CustomerRole.TxCenterQuoteApprover]: [],
      [CustomerRole.TxCenterOrganApprover]: [],
      [CustomerRole.TxCenterOfferCoordinator]: [],
    },
  ],
  [OrganizationUserPermissions.Manage]: [
    {
      [OrganizationUserManagePermissions.UserEdit]: [CustomerRole.TxCenterAdministrator],
      [OrganizationUserManagePermissions.UserDelete]: [CustomerRole.TxCenterAdministrator],
      [OrganizationUserManagePermissions.UserActiveToggle]: [CustomerRole.TxCenterAdministrator],
    },
  ],
};
const ORGANISATION_PERMISSIONS: Record<
  OrganizationPermissions | OrganizationUserPermissions | DefaultOrganAcceptanceCriteriaPermissions,
  (CustomerRoleT | OrganizationUserPermissionsT)[]
> = {
  [OrganizationPermissions.View]: allRoles,
  [OrganizationPermissions.Manage]: [CustomerRole.TxCenterAdministrator],
  ...ORGANISATION_USER_PERMISSIONS,
  ...DEFAULT_ORGAN_ACCEPTANCE_CRITERIA_PERMISSIONS,
};
const ARCHIVE_PERMISSIONS_VIEW = [
  CustomerRole.TxCenterAdministrator,
  CustomerRole.ReceivingSurgeon,
  CustomerRole.AdminAuditor,
];
const ARCHIVE_PERMISSIONS: Record<ArchivePermissions, CustomerRoleT[]> = {
  [ArchivePermissions.View]: ARCHIVE_PERMISSIONS_VIEW,
  [ArchivePermissions.CaseDetailsViewTab]: ARCHIVE_PERMISSIONS_VIEW,
  [ArchivePermissions.CaseTeamViewTab]: excludeRole(
    ARCHIVE_PERMISSIONS_VIEW,
    CustomerRole.TxCenterAdministrator,
    CustomerRole.AdminAuditor,
  ),
  [ArchivePermissions.ClinicalLogViewTab]: excludeRole(
    ARCHIVE_PERMISSIONS_VIEW,
    CustomerRole.TxCenterAdministrator,
    CustomerRole.AdminAuditor,
  ),
  [ArchivePermissions.ActivitiesViewTab]: excludeRole(
    ARCHIVE_PERMISSIONS_VIEW,
    CustomerRole.TxCenterAdministrator,
    CustomerRole.AdminAuditor,
  ),
};
const OPO_PERMISSIONS: Record<OPOsPermissions, CustomerRoleT[]> = {
  [OPOsPermissions.View]: [
    CustomerRole.TxCenterAdministrator,
    CustomerRole.AdminAuditor,
    CustomerRole.TxCenterCoordinator,
  ],
  [OPOsPermissions.Manage]: [CustomerRole.TxCenterAdministrator, CustomerRole.TxCenterCoordinator],
};
const TRIAL_PERMISSIONS: Record<TrialPermissions, CustomerRoleT[]> = {
  [TrialPermissions.RandomizationView]: allRoles,
  [TrialPermissions.RandomizationManage]: [CustomerRole.TxCenterCoordinator, CustomerRole.ReceivingSurgeon],
};

const opoAllRoles: OpoRoleOnly[] = [
  OpoRoleEnum.OpoAdmin,
  OpoRoleEnum.OpoRetrievalSurgeon,
  OpoRoleEnum.OpoTransplantCoordinator,
  OpoRoleEnum.OpoTransplantSupport,
];

const opoCanManageRoles: OpoRoleOnly[] = [OpoRoleEnum.OpoAdmin, OpoRoleEnum.OpoTransplantCoordinator];

const opoClinicalRoles: OpoRoleOnly[] = [
  OpoRoleEnum.OpoAdmin,
  OpoRoleEnum.OpoRetrievalSurgeon,
  OpoRoleEnum.OpoTransplantCoordinator,
];

const OFFER_PERMISSIONS: Record<OfferPermissions, CustomerRoleT[]> = {
  [OfferPermissions.Manage]: [CustomerRole.TxCenterOfferCoordinator],
};

export const PERMISSIONS_BY_ROLES: Record<RootPermissionType, CustomerRoleT[] | RoleMappingT[]> = {
  ...CASE_PERMISSIONS,
  ...ORGANISATION_PERMISSIONS,
  ...ARCHIVE_PERMISSIONS,
  [InvoicePermissions.ReadInvoice]: [CustomerRole.TxCenterAdministrator, CustomerRole.AdminAuditor],
  ...OPO_PERMISSIONS,
  ...TRIAL_PERMISSIONS,

  // OPO role additions — merged with existing customer role entries
  [CasePermissions.Create]: [...canManageRoles, ...opoCanManageRoles],
  [CasePermissions.CaseDetailsViewTab]: [...allRoles, ...opoAllRoles],
  [CasePermissions.CaseDetailsManageTab]: [...canManageRoles, ...opoCanManageRoles],
  [CasePermissions.CaseTeamViewTab]: [...allRoles, ...opoAllRoles],
  [CasePermissions.CaseTeamManageTab]: [...canManageRoles, ...opoCanManageRoles],
  [CasePermissions.CaseClinicalLogViewTab]: [...excludeRole(allRoles, CustomerRole.AdminAuditor), ...opoAllRoles],
  [CasePermissions.CaseClinicalLogManageTab]: [...canManageRoles, ...opoClinicalRoles],
  [CasePermissions.CaseActivitiesViewTab]: [...allRoles, ...opoAllRoles],
  [TrialPermissions.RandomizationView]: [...allRoles, ...opoAllRoles],
  [AttachmentSectionPermissions.View]: [
    ...excludeRole(allRoles, CustomerRole.TxCenterTransplantSupport),
    ...opoAllRoles,
  ],
  [OrganizationPermissions.View]: [...allRoles, ...opoAllRoles],
  [OrganizationPermissions.Manage]: [CustomerRole.TxCenterAdministrator, OpoRoleEnum.OpoAdmin],
  [OrganizationUserPermissions.View]: [...allRoles, ...opoAllRoles],
  // OPO admin gets Create as a simple role string — nested mapping keys are CustomerRole values
  // which getPermissionsList checks via Object.values(CustomerRole), excluding OpoRoleEnum.
  [OrganizationUserPermissions.Create]: [
    ...ORGANISATION_USER_PERMISSIONS[OrganizationUserPermissions.Create],
    OpoRoleEnum.OpoAdmin,
  ],
  [OrganizationUserPermissions.Manage]: [
    ...ORGANISATION_USER_PERMISSIONS[OrganizationUserPermissions.Manage],
    {
      [OrganizationUserManagePermissions.UserEdit]: [OpoRoleEnum.OpoAdmin],
      [OrganizationUserManagePermissions.UserDelete]: [OpoRoleEnum.OpoAdmin],
      [OrganizationUserManagePermissions.UserActiveToggle]: [OpoRoleEnum.OpoAdmin],
    },
  ],
  ...OFFER_PERMISSIONS,
};
